import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';

export interface AmbiguousReplaceDialogContext {
  /** Names the project holds more than once — ALL of them, so «заменить все» never covers a name the
   *  person was not shown. The list scrolls; the window does not grow. */
  readonly names: readonly string[];
  /** How many project rows the WIDEST of those names resolves to — the number in «заменить все N». */
  readonly matches: number;
  /** Every row under every listed name: what «заменить все» rewrites, on screen before the click. */
  readonly rows: number;
  /** Each listed name has ONE row standing in the open album — that row can be replaced on its own,
   *  leaving the other same-name photos untouched. */
  readonly aimed: boolean;
}

export type AmbiguousReplaceDecision = 'replace-aimed' | 'replace-all' | 'as-new';

/**
 * The way out of «под одним именем несколько фотографий» (FKS-11259). Until this window existed the drop
 * quietly became one more copy and the person had no way back inside the product. It is NOT behind the
 * confirm-replace flag: a silent extra copy is the bug, not a confirmation nicety. Closing it (Esc, the
 * backdrop, «Пропустить») drops only these names — the rest of the drop keeps uploading.
 */
@Component({
  selector: 'app-ambiguous-replace-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, DialogSheetComponent],
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 class="mb-1 text-lg font-bold text-ink">{{ title() }}</h2>
        <p class="mb-2 text-base leading-relaxed text-ink">{{ lead() }}</p>
        <ul class="mb-3 max-h-56 space-y-1 overflow-y-auto pr-1">
          <!-- track $index: one drop can legitimately list two names that differ only in case. -->
          @for (name of ctx.names; track $index) {
            <!-- Wraps, never truncates: on a phone the cut lands mid-arrow and hides the stored spelling. -->
            <li class="break-words text-sm text-ink">{{ name }}</li>
          }
        </ul>
        <p class="mb-5 text-base leading-relaxed text-ink">{{ explanation() }}</p>
        <div class="flex flex-col gap-2">
          @if (ctx.aimed) {
            <button hlmBtn type="button" variant="default" (click)="close('replace-aimed')">{{ aimedLabel() }}</button>
            <button hlmBtn type="button" variant="outline" (click)="close('replace-all')">{{ replaceLabel() }}</button>
          } @else {
            <button hlmBtn type="button" variant="default" (click)="close('replace-all')">{{ replaceLabel() }}</button>
          }
          <button hlmBtn type="button" variant="outline" (click)="close('as-new')">{{ asNewLabel() }}</button>
          <button hlmBtn type="button" variant="ghost" (click)="cancel()">Пропустить эти файлы</button>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class AmbiguousReplaceDialogComponent {
  protected readonly hostClass = dialogSheetHostClass('s', 'p-6');
  protected readonly ctx = injectBrnDialogContext<AmbiguousReplaceDialogContext>();
  private readonly dialogRef = inject(BrnDialogRef);

  private readonly single = computed(() => this.ctx.names.length === 1);

  protected readonly title = computed(() =>
    this.single() ? 'В проекте несколько фото с таким именем' : 'В проекте несколько фото с такими именами',
  );

  protected readonly lead = computed(() =>
    this.single()
      ? `Это имя занято сразу несколькими фото (${this.ctx.matches}) — какое из них заменить, определить нельзя:`
      : `Эти имена заняты сразу несколькими фото (всего ${this.ctx.rows}) — какие из них заменить, определить нельзя:`,
  );

  protected readonly explanation = computed(() => {
    const replaceAll = this.single()
      ? `Можно заменить все ${this.ctx.matches} фото с этим именем — тогда везде окажется новый файл.`
      : `Можно заменить все одноимённые фото — это ${this.ctx.rows} фото, и везде окажутся новые файлы.`;
    const aimed = this.ctx.aimed
      ? this.single()
        ? 'Можно заменить только то фото, которое стоит в открытом альбоме — остальные одноимённые останутся как есть. '
        : 'Можно заменить только те фото, которые стоят в открытом альбоме — остальные одноимённые останутся как есть. '
      : '';
    const asNew = this.single()
      ? ' Или загрузить его отдельным фото — в проекте станет ещё одна копия.'
      : ' Или загрузить их отдельными фото — в проекте станет по одной копии.';
    // A rewrite is not undoable from the interface: it moves the tags and likes of the row it lands on and
    // takes that photo out of the project. The window says so BEFORE the click, not in a toast after it.
    return `${aimed}${replaceAll}${asNew} Заменённые фото вернуть не получится.`;
  });

  protected readonly aimedLabel = computed(() =>
    this.single() ? 'Заменить фото из альбома' : `Заменить фото из альбома (${this.ctx.names.length})`,
  );

  protected readonly replaceLabel = computed(() =>
    this.single() ? `Заменить все ${this.ctx.matches}` : `Заменить все одноимённые (${this.ctx.rows})`,
  );

  protected readonly asNewLabel = computed(() => (this.single() ? 'Загрузить как новое фото' : 'Загрузить как новые фото'));

  protected close(decision: AmbiguousReplaceDecision): void {
    this.dialogRef.close(decision);
  }

  protected cancel(): void {
    this.dialogRef.close(undefined);
  }
}
